/**
 * The Renews cell's hover card (#1141): what the renewal does to one licence line's price and margin.
 */
import { Tooltip } from '@patchbay/ui'
import type { LicenceMarginRow } from '../../../lib/api/generated'
import { formatMoney } from '../charts'
import { dateLabel } from '../shared'

const num = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

export type RenewalChange = {
  days: number | null
  costNow: number | null
  costRenewal: number | null
  costDelta: number | null
  costDeltaPct: number | null
  marginPctNow: number | null
  marginPctRenewal: number | null
  marginPtsDelta: number | null
  unitMarginNow: number | null
  unitMarginRenewal: number | null
  qty: number | null
  qtySource: 'billed' | 'pax8' | null
  lineDelta: number | null
}

/**
 * What the renewal does to one licence line (#1141). Every figure is null when an input is missing: the hover says
 * "unknown", never 0 (hard rule 10). The billed rate is assumed unchanged across the renewal; the line effect uses the
 * billed quantity, else Pax8's.
 */
export function renewalChange(row: LicenceMarginRow, today: Date = new Date()): RenewalChange {
  const costNow = num(row.cost_now)
  const costRenewal = num(row.cost_at_renewal)
  const billed = num(row.billed_rate)
  const marginPctNow = num(row.margin_pct_now)
  const marginPctRenewal = num(row.margin_pct_at_renewal)
  const costDelta = costNow !== null && costRenewal !== null ? costRenewal - costNow : null
  const unitMarginNow = billed !== null && costNow !== null ? billed - costNow : null
  const unitMarginRenewal = billed !== null && costRenewal !== null ? billed - costRenewal : null
  const qtySource = row.billed_qty != null ? 'billed' : row.pax8_qty != null ? 'pax8' : null
  const qty = row.billed_qty ?? row.pax8_qty ?? null
  const unitDelta = unitMarginNow !== null && unitMarginRenewal !== null ? unitMarginRenewal - unitMarginNow : null
  let days: number | null = null
  if (row.renews) {
    const end = new Date(`${row.renews}T12:00:00`)
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12)
    if (!Number.isNaN(end.getTime())) days = Math.round((end.getTime() - start.getTime()) / 86_400_000)
  }
  return {
    days,
    costNow,
    costRenewal,
    costDelta,
    costDeltaPct: costDelta !== null && costNow ? (costDelta / costNow) * 100 : null,
    marginPctNow,
    marginPctRenewal,
    marginPtsDelta: marginPctNow !== null && marginPctRenewal !== null ? marginPctRenewal - marginPctNow : null,
    unitMarginNow,
    unitMarginRenewal,
    qty,
    qtySource,
    lineDelta: unitDelta !== null && qty !== null ? unitDelta * qty : null,
  }
}

const UNKNOWN = 'unknown'
const signed = (n: number, fmt: (x: number) => string) => `${n > 0 ? '+' : n < 0 ? '−' : '±'}${fmt(Math.abs(n))}`
const pct1 = (n: number) => `${n.toFixed(1)}%`
const pts1 = (n: number) => `${n.toFixed(1)} pts`

function RenewalCard({ c, renews }: { c: RenewalChange; renews: string }) {
  const when =
    c.days === null
      ? dateLabel(renews)
      : c.days < 0
        ? `${dateLabel(renews)} (${-c.days} days ago)`
        : `${dateLabel(renews)} (in ${c.days} days)`
  const money = (n: number | null) => (n === null ? UNKNOWN : formatMoney(n))
  const pct = (n: number | null) => (n === null ? UNKNOWN : pct1(n))
  return (
    <div className="grid min-w-56 gap-1.5 text-xs" data-testid="renewal-card">
      <div className="font-medium">Renews {when}</div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 tabular-nums">
        <dt className="opacity-75">Our cost</dt>
        <dd>
          {money(c.costNow)} → {money(c.costRenewal)}
          {c.costDelta === null ? null : (
            <>
              {' '}
              ({signed(c.costDelta, formatMoney)}
              {c.costDeltaPct === null ? '' : `, ${signed(c.costDeltaPct, pct1)}`})
            </>
          )}
        </dd>
        <dt className="opacity-75">Margin</dt>
        <dd>
          {pct(c.marginPctNow)} → {pct(c.marginPctRenewal)}
          {c.marginPtsDelta === null ? null : ` (${signed(c.marginPtsDelta, pts1)})`}
        </dd>
        <dt className="opacity-75">Per licence</dt>
        <dd>
          {money(c.unitMarginNow)} → {money(c.unitMarginRenewal)}
        </dd>
        <dt className="opacity-75">Line effect</dt>
        <dd>
          {c.lineDelta === null || c.qty === null
            ? UNKNOWN
            : `${signed(c.lineDelta, formatMoney)} per billing period across ${c.qty} ${c.qtySource === 'pax8' ? 'Pax8' : 'billed'} licence${c.qty === 1 ? '' : 's'}`}
        </dd>
      </dl>
      <div className="opacity-75">Assumes we keep billing the current rate.</div>
    </div>
  )
}

/** The renewal date; for cost readers, hover or focus shows what the renewal does to price and margin (#1141). */
export function RenewalCell({ row, cost }: { row: LicenceMarginRow; cost: boolean }) {
  if (!row.renews) return <span className="text-muted-foreground">—</span>
  if (!cost) return <>{dateLabel(row.renews)}</>
  const c = renewalChange(row)
  return (
    <Tooltip content={<RenewalCard c={c} renews={row.renews} />}>
      <button
        type="button"
        className="cursor-help underline decoration-dotted underline-offset-4 focus-visible:outline-2 focus-visible:outline-ring"
        aria-label={`Renews ${dateLabel(row.renews)}: show the price and margin change`}
      >
        {dateLabel(row.renews)}
      </button>
    </Tooltip>
  )
}
